import { useEffect, useMemo, useRef } from "react";
import { ChevronRight, Search } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { formatPhp } from "../lib/money";
import { MobileAccountBreadcrumb } from "./MobileAccountBreadcrumb";
import { Input } from "./beui/Input";
import { Button } from "./beui/Button";
import "./MobileContractsList.css";

/** Display projection only; Contracts owns phase derivation and authorized navigation. */
export type MobileContractRow = {
  id: string;
  title: string;
  counterparty: string;
  roleLabel: string;
  stage: string;
  statusLabel: string;
  statusClass: string;
  amount: number | null;
  valueLabel?: string;
  href: string;
  createdAt: string;
};

type MobileContractsListProps = {
  rows: readonly MobileContractRow[];
  stages: readonly { key: string; label: string }[];
  selectedStage: string;
  query: string;
  onStageChange: (stage: string) => void;
  onQueryChange: (query: string) => void;
  onOpen: (href: string) => void;
};

export function MobileContractsList({ rows, stages, selectedStage, query, onStageChange, onQueryChange, onOpen }: MobileContractsListProps) {
  const { t, lang } = useI18n();
  const stageRailRef = useRef<HTMLElement>(null);
  const selectedStageRef = useRef<HTMLButtonElement>(null);
  // Keep the selected stage visible after detail returns, language changes, and rotation.
  useEffect(() => {
    const revealStage = () => selectedStageRef.current?.scrollIntoView({ block: "nearest", inline: "nearest" });
    const observer = new ResizeObserver(revealStage);
    if (stageRailRef.current) observer.observe(stageRailRef.current);
    revealStage();
    return () => observer.disconnect();
  }, [selectedStage, lang]);
  const visible = useMemo(() => {
    const search = query.trim().toLocaleLowerCase();
    return rows.filter((row) => row.stage === selectedStage
      && (!search || `${row.title} ${row.counterparty}`.toLocaleLowerCase().includes(search)))
      .sort((left, right) => new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime());
  }, [rows, selectedStage, query]);
  return (
    <section className="mobile-contracts-list">
      <MobileAccountBreadcrumb currentLabel={t("dashboard.accountMenu.contracts")} count={rows.length} />
      <div className="mobile-contracts-search">
        <Input type="search" value={query} onChange={onQueryChange} leftIcon={<Search className="h-4 w-4" />} classNames={{ field: "min-h-11", input: "text-xs" }}
          aria-label={t("contracts.mobile.search")} placeholder={t("contracts.mobile.search")} />
      </div>
      <nav ref={stageRailRef} className="mobile-contracts-stages" aria-label={t("contracts.mobile.stage")}>
        {stages.map(({ key, label }) => <Button variant={selectedStage === key ? "primary" : "outline"} pressScale={0.98} type="button" key={key} aria-pressed={selectedStage === key}
          ref={selectedStage === key ? selectedStageRef : undefined}
          onClick={() => onStageChange(key)}>{label}</Button>)}
      </nav>
      {query && <button type="button" className="mobile-contracts-clear" onClick={() => onQueryChange("")}>{t("contracts.mobile.clearSearch")}</button>}
      <div className="mobile-contracts-rows">
        {visible.length === 0 && <p className="mobile-contracts-empty" role="status">{t(rows.length ? "contracts.mobile.noMatch" : "contracts.mobile.empty")}</p>}
        {visible.map((row) => <MobileContractListRow key={row.id} row={row} lang={lang} onOpen={onOpen} />)}
      </div>
    </section>
  );
}

function MobileContractListRow({ row, lang, onOpen }: { row: MobileContractRow; lang: "en" | "tl"; onOpen: (href: string) => void }) {
  const initials = row.counterparty.trim().split(/\s+/).slice(0, 2).map((part) => part.charAt(0)).join("").toUpperCase() || "?";
  const created = new Date(row.createdAt);
  const date = Number.isNaN(created.getTime()) ? "" : created.toLocaleDateString(lang === "tl" ? "fil-PH" : "en-PH", { month: "short", day: "numeric" });
  return (
    <button type="button" className="mobile-contracts-row" onClick={() => onOpen(row.href)} aria-label={row.title}>
      <span className="mobile-contracts-avatar" aria-hidden="true">{initials}</span>
      <div className="mobile-contracts-copy">
        <h2>{row.title}</h2>
        <p>{row.counterparty || "—"} · {row.roleLabel}</p>
        <span className={`mobile-contracts-status ${row.statusClass}`}>{row.statusLabel}</span>
      </div>
      <div className="mobile-contracts-value"><strong>{row.valueLabel ?? (row.amount == null ? "—" : formatPhp(row.amount))}</strong><span>{date}</span></div>
      <ChevronRight aria-hidden="true" />
    </button>
  );
}
